import { useEffect, useState } from 'react';
import { getProviderIntegrationHealth } from '../../api/providerApi';
import { visibleIntegrationConnectors } from './visibleIntegrationConnectors';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { formatDateTime } from '../../utils/format';

function statusLabel(status) {
  if (!status) return '—';
  return status.charAt(0) + status.slice(1).toLowerCase();
}

export function IntegrationsWebhooksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await getProviderIntegrationHealth();
        if (!cancelled) setItems(visibleIntegrationConnectors(response.items));
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || 'Unable to load integration health.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const columns = [
    {
      key: 'name',
      header: 'Connector',
      render: (row) => <span className="font-semibold text-eorig-ink">{row.name}</span>,
    },
    {
      key: 'connector_type',
      header: 'Type',
      render: (row) => (
        <span className="rounded-md bg-eorig-surface px-2 py-1 text-[11px] text-eorig-muted">
          {row.connector_type}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill value={row.status} label={statusLabel(row.status)} />,
    },
    {
      key: 'latency_ms',
      header: 'Latency',
      render: (row) => (
        <span className="tabular-nums text-eorig-muted">
          {row.latency_ms != null ? `${row.latency_ms} ms` : '—'}
        </span>
      ),
    },
    {
      key: 'failure_count',
      header: 'Failures',
      render: (row) => <span className="tabular-nums">{row.failure_count ?? 0}</span>,
    },
    {
      key: 'last_checked_at',
      header: 'Last checked',
      render: (row) => (
        <span className="text-eorig-muted">{formatDateTime(row.last_checked_at)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} />
      ) : null}
      <DataTable
        columns={columns}
        rows={items}
        rowKey={(row) => `${row.connector_type}-${row.name}`}
        loading={loading}
        emptyMessage="No connectors returned."
        minWidth={640}
      />
    </div>
  );
}
